import type { PostHog } from 'posthog-js';
import { useSyncExternalStore } from 'react';
import { rt } from './runtime.ts';

/**
 * PostHog in the browser, only after the visitor says yes. Until then nothing is loaded, stored or sent:
 * the library itself is fetched on consent. The choice is kept in local storage (needed to remember it),
 * and can be changed from any footer. Game actions are sent separately, by the server, for players who
 * allow it (the tally); signed-in players are identified by player id so the two line up.
 */
export type Consent = 'yes' | 'no' | null;

const KEY = 'wyn.analytics';
const listeners = new Set<() => void>();
let consent: Consent = read();
let ph: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
let identity: { id: string; props: Record<string, string> } | null = null;
let identified: string | null = null;

function read(): Consent {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'yes' || v === 'no' ? v : null;
  } catch {
    return null;
  }
}

/** Whether this stage has analytics at all. Locally only when VITE_POSTHOG_KEY is set, so tests never see a banner. */
export const analyticsConfigured = () => Boolean(rt().config.posthog);

export function useConsent(): Consent {
  return useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), () => consent);
}

export function setConsent(next: Consent): void {
  consent = next;
  try {
    if (next) localStorage.setItem(KEY, next);
    else localStorage.removeItem(KEY);
  } catch {
    // Private windows may refuse; the choice then lasts for this visit.
  }
  for (const l of listeners) l();
  if (next === 'yes') void load();
  if (next === 'no' && ph) {
    ph.opt_out_capturing();
    ph.reset();
    identified = null;
  }
}

/** Loads PostHog if the visitor has already said yes. Called once at start-up. */
export function startAnalytics(): void {
  if (consent === 'yes') void load();
}

function load(): Promise<PostHog | null> {
  const cfg = rt().config.posthog;
  if (!cfg) return Promise.resolve(null);
  loading ??= import('posthog-js').then(({ default: posthog }) => {
    posthog.init(cfg.key, {
      api_host: cfg.host,
      ui_host: 'https://eu.posthog.com',
      // Anonymous visitors stay anonymous: a person profile only for a player who allows it.
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      // Clicks on links and buttons only; never what anyone types.
      autocapture: { element_allowlist: ['a', 'button'] },
      disable_session_recording: true,
      mask_personal_data_properties: true,
      respect_dnt: true,
    });
    posthog.register({ stage: rt().config.stage });
    if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
    ph = posthog;
    applyIdentity();
    return posthog;
  });
  return loading;
}

function applyIdentity(): void {
  if (!ph || consent !== 'yes') return;
  if (identity && identity.id !== identified) {
    ph.identify(identity.id, identity.props);
    identified = identity.id;
  } else if (!identity && identified) {
    ph.reset();
    identified = null;
  }
}

/**
 * Links this browser's visits to the signed-in player, if their analytics setting is on (their choice,
 * or on by default only for adults). Otherwise the visit stays anonymous. Null on sign-out.
 */
export function identifyPlayer(p: { id: string; analytics: 'on' | 'off'; calling: string; homeTown: string } | null): void {
  identity = p && p.analytics === 'on' ? { id: p.id, props: { calling: p.calling, home_town: p.homeTown } } : null;
  applyIdentity();
}

/** On sign-out: the next person on this browser starts anonymous. */
export function forgetPlayer(): void {
  identity = null;
  if (ph && identified) ph.reset();
  identified = null;
}
